import './jquery.selector.focusable';
import './scrollTrigger';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import _debounce from 'lodash/debounce';
import { tabIndexManager } from './_utils';
import { toggleVideoPlayback } from './video';

const heroClassName = 'TabbedHero';
const classes = {
	navAutoOpen: 'NavigationCAW--open',
	activeSearch: 'active-search',
	navAtTop: 'Navigation--atTop',
};
const selectors = {
	tabbedHero: `.${heroClassName}`,
	iconCTA: `.TabbedHero-icons .CTA`,
	hero: '.Hero',
	tab: '.TabbedHero-tab',
	bgImage: '.BackgroundImage, .Hero-video',
	heroVideo: '.Hero-video',
	container: '.TabbedHero-container',
	scroll: '.TabbedHero-scroll',
	scrollButton: '.TabbedHero-scroll button',
	scrollLarge: '.TabbedHero-scroll-large',
	icons: '.TabbedHero-icons',
	icon: '.TabbedHero-icon',
	activeRect: '.TabbedHero-activeRect',
	navBar: '.Navigation-bar',
	contentWrapper: '.TabbedHero-content',
	alertBanner: '.AlertBanner',
	navAutoOpen: `.${classes.navAutoOpen}`,
	alertClose: '.Banner-closeButton',
	navCAW: '.NavigationCAW',
	video: '.Hero-video video',
	videoButton: '.Video-playButton',
	interstitialWrapper: '#Interstitial-wrapper',
	headingContainer: '.TabbedHero-headingContainer',
	hotelSearchBar: `.hotelSearchBar`,
	hotelSearchBarInput: `.hotelSearchBar input`,
	hotelSearchResults: '.hotelSearchBar-results',
	footer: '.Navigation-sticky-footer',
	iconText: '.TabbedHero-icon-text',
	navAtTop: `.${classes.navAtTop}`,
	nav: '.Navigation--hasTabbedHero',
	interstitialOfferBanner: '.InterstitialOfferBanner',
	logo: `.${heroClassName}-logo`,
};

export default function tabbedHero() {
	document.querySelectorAll(selectors.tabbedHero).forEach((hero, heroIndex) => {
		// bypass auto scrolling.
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}
		//don't auto open the CAW when there is a fullscreen hero
		const navCAWAutoOpening = document.querySelector(selectors.navAutoOpen);
		if (navCAWAutoOpening) {
			navCAWAutoOpening.classList.remove(classes.navAutoOpen);
		}
		const $hero = $(hero);
		const maxTabIndex = $hero.data('maxtabindex');

		$hero.find(selectors.scrollButton).on('click', () => {
			$('html, body').animate({ scrollTop: `+=${window.innerHeight / 2}px` }, 800);
		});
		const $tabs = $hero.find(selectors.tab);
		$tabs.each((_i, innerTab) => {
			const $innerTab = $(innerTab);
			$innerTab.append($innerTab.find(selectors.videoButton));
		});

		const containers = hero.querySelectorAll(selectors.container);
		const $icons = $hero.find(selectors.icon);
		const navBar = document.querySelector(selectors.navBar);
		const navCaw = document.querySelector(selectors.navCAW);
		const logo = document.querySelector(selectors.logo);
		window.addEventListener('resize', () => {
			if (window.scrollY === 0) {
				const nav = document.querySelector(selectors.nav);
				if (nav) {
					nav.classList.add(classes.navAtTop);
				}
			}
		});
		let lastIndex = 0;
		let prevFadeIndex = -1;
		let setTabIndexWhenFading = true;
		const fadeFn = () => {
			if (lastIndex !== prevFadeIndex) {
				const isLarge = window.innerWidth >= 1100;
				lastIndex = isLarge ? lastIndex : 0;
				prevFadeIndex = lastIndex;
				$tabs.css({
					opacity: 0,
					zIndex: -1,
				});

				$($tabs.get(lastIndex)).css({
					opacity: 1,
					zIndex: 1,
				});
				const icon = $icons.get(lastIndex);
				if (setTabIndexWhenFading) {
					$tabs.find(':focusable:not(.TabbedHero-icon-cta)').attr('tabindex', '-1');
				}
				$icons.removeClass('active');
				if (icon) {
					$(icon).addClass('active');
					$(hero)
						.find(selectors.activeRect)
						.css('left', icon.getBoundingClientRect().left - 50);
				}
				$tabs.each((i, innerTab) => {
					const videoElement = innerTab.querySelector(selectors.video);
					let toggle = false;
					if (videoElement) {
						if (videoElement.paused && i === lastIndex) {
							toggle = true;
						} else if (!videoElement.paused && i !== lastIndex) {
							toggle = true;
						}
					}
					if (toggle) {
						toggleVideoPlayback(
							innerTab.querySelector(selectors.videoButton),
							videoElement
						);
					}
				});
				if (
					!$tabs.get(lastIndex).contains(document.activeElement) &&
					!$icons.get(lastIndex).contains(document.activeElement)
				) {
					document.activeElement.blur();
				}

				const $activeHeading = $($tabs.get(lastIndex)).find(
					`${selectors.headingContainer} .Heading-title`
				);
				logo.style.color = $activeHeading.css('color');

				window.dispatchEvent(new Event('tabbedHeroSwitched'));
			}
		};
		const components = hero.querySelectorAll(
			`${selectors.container}, ${selectors.scroll}, ${selectors.icons}, ${selectors.videoButton}`
		);
		ScrollTrigger.saveStyles(
			`${selectors.hero}, ${selectors.container}, ${selectors.scroll}, ${selectors.icons}, ${selectors.bgImage}, ${selectors.scrollLarge}, ${selectors.videoButton}, ${selectors.logo}`
		);

		ScrollTrigger.matchMedia({
			// large
			'(min-width: 1100px)': function () {
				const heroImages = document.querySelectorAll('.Hero .lazypreload img');
				heroImages.forEach(heroImage => heroImage.setAttribute('loading', 'eager'));
				const tlScroll = gsap.timeline({
					id: `tabbedHeroDesktop-${heroIndex}`,
					scrollTrigger: {
						trigger: hero,
						start: 'top+=1 top',
						end: window.innerHeight / 2,
						scrub: 0.5,
						onEnter: () => {
							gsap.to(hero.querySelectorAll(`${selectors.scrollLarge}`), {
								height: 0,
								minHeight: 0,
								duration: 1,
								onComplete: () => {
									ScrollTrigger.refresh();
								},
							});
						},
						onLeaveBack: () => {
							window.dispatchEvent(new Event('cawComponentClose'));

							setTimeout(() => {
								gsap.to(navBar, {
									translateY: -121,
									duration: 0.1,
								});
								gsap.to(components, {
									opacity: 1,
									duration: 0.1,
								});
								gsap.to(hero.querySelectorAll(selectors.bgImage), {
									scale: 1,
									duration: 0.1,
								});
							}, 510);
						},
					},
				});

				tlScroll
					.addLabel('start')
					.to(
						components,
						{
							opacity: 0,
						},
						'start'
					)
					.to(
						[navBar, navCaw],
						{
							translateY: 0,
						},
						'start'
					)
					.to(
						hero.querySelectorAll(selectors.bgImage),
						{
							scale: 1.2,
						},
						'start'
					);
			},
		});

		const toggleAnimations = enable => {
			ScrollTrigger.getAll().forEach(st => {
				if (
					st &&
					st.animation &&
					st.animation.vars &&
					st.animation.vars.id &&
					st.animation.vars.id.includes('tabbedHero')
				) {
					if (enable) {
						st.enable();
					} else {
						st.disable(true);
					}
				}
			});
		};
		$(document).on('startingCurtainMode', () => toggleAnimations(false));
		$(document).on('leftCurtainMode', () => toggleAnimations(true));
		const interstitialWrapper = document.querySelector(selectors.interstitialWrapper);
		if (interstitialWrapper && interstitialWrapper.style.position === 'fixed') {
			toggleAnimations(false);
		}

		hero.querySelectorAll(selectors.iconCTA).forEach((cta, ctaIndex) => {
			cta.onmouseover = () => {
				setTimeout(() => {
					lastIndex = ctaIndex;
					fadeFn();
				}, 500);
			};
			cta.onfocus = () => {
				lastIndex = ctaIndex;
				tabIndexManager.reinstate($tabs.find('[data-tabindex]'), true);
				tabIndexManager.reinstate($icons.find('[data-tabindex]'), true);
				setTabIndexWhenFading = false;
				window.scrollTo(window.scrollX, 0);
				fadeFn();
			};
		});

		containers.forEach(container => {
			const searchBar = container.querySelector(selectors.hotelSearchBar);
			if (searchBar) {
				const $container = $(container);
				const $searchBar = $(searchBar);
				window.addEventListener('hotelSearchChanged', () => {
					if ($searchBar.hasClass('active')) {
						$container.addClass(classes.activeSearch);
					} else {
						$container.removeClass(classes.activeSearch);
					}
				});
			}
		});

		window.addEventListener(
			'resize',
			_debounce(() => {
				fadeFn();
			}, 100)
		);

		let focusWatcher;
		window.addEventListener(
			'focus',
			() => {
				const $active = $(document.activeElement);
				const tabindex = $active.data('tabindex');
				if (hero.contains(document.activeElement) && tabindex) {
					setTabIndexWhenFading = false;
					if (focusWatcher) {
						clearTimeout(focusWatcher);
					}
					const tab = $active.parents(selectors.tab);
					if (tab.length && tab.css('z-index') !== '1') {
						lastIndex--;
						fadeFn();
					}
				}
			},
			true
		);
		window.addEventListener(
			'blur',
			() => {
				if (!setTabIndexWhenFading) {
					focusWatcher = setTimeout(function () {
						$tabs.find('[data-tabindex]').attr('tabindex', -1);
						$icons.find('[data-tabindex]').attr('tabindex', 0);
					}, 200);
				}

				setTabIndexWhenFading = true;
			},
			true
		);
		document.addEventListener('keydown', function (event) {
			let code = event.key && event.key === 'Tab' ? 9 : event.keyCode || event.which;
			if (code === 9) {
				const tabIndex = $(document.activeElement).data('tabindex');

				if (tabIndex === maxTabIndex && !event.shiftKey) {
					const focusableBodyElem = $('#main').find(':focusable[tabindex!="-1"]')[0];
					if (focusableBodyElem) {
						focusableBodyElem.focus();
					}
					event.preventDefault();
				}
			}
		});
		//make sure everything is setup on load
		window.requestAnimationFrame(() => {
			tabIndexManager.disable($tabs.find(':focusable[tabindex!="-1"]'));
			tabIndexManager.disable($icons.find(':focusable[tabindex!="-1"]'), 0);
			fadeFn();
		});
	});
}
